// SPEC-092 M6 — клиент ручек /api/auth/store-logins (экран «ЛК магазинов»).
// Server-safe (без "use client"): форма контракта и маппинг статусов в русские
// сообщения живут здесь одни, экран и тесты берут их отсюда.
//
// Посадка по CSRF — как у остальных POST/PATCH SPA (сотрудники): same-origin,
// cookie сессии едет через credentials: "include", csrf_token не шлём
// (на контракте он опционален).

import { z } from "zod";

const STORE_LOGINS_ENDPOINT = "/api/auth/store-logins";

/** Ответ создания/правки ЛК точки. Телефон-логин генерирует СЕРВЕР — узнать его
 *  можно только отсюда (или из списка), пароль наружу не возвращается. */
export const storeLoginResultSchema = z.object({
  user_id: z.number().int(),
  storage_id: z.number().int(),
  storage_name: z.string().nullish(),
  phone: z.string(),
  is_active: z.boolean(),
});

export type StoreLoginResult = z.infer<typeof storeLoginResultSchema>;

export type CreateResult =
  | { ok: true; login: StoreLoginResult }
  | { ok: false; message: string };

export type MutationResult = { ok: true } | { ok: false; message: string };

/** Пароль ЛК магазина — РОВНО 6 цифр (D15, зеркало серверной проверки). */
export function isValidStorePassword(value: string): boolean {
  return /^\d{6}$/.test(value);
}

/** Телефон-логин по-человечески: +70000000001 → +7 000 000-00-01. */
export function formatStorePhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (digits.length !== 11) return phone;
  const d = digits.slice(1);
  return `+7 ${d.slice(0, 3)} ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
}

/** Статус создания ЛК → сообщение пользователю. */
export function createErrorMessage(status: number): string {
  switch (status) {
    case 401:
      return "Сессия истекла. Обновите страницу и войдите снова.";
    case 403:
      return "Нет доступа";
    case 404:
      return "Точка не найдена";
    case 409:
      return "У точки уже есть ЛК";
    case 422:
      return "Пароль — ровно 6 цифр";
    default:
      return "Не удалось создать ЛК, попробуйте позже.";
  }
}

/** Статус правки ЛК → сообщение пользователю. */
export function updateErrorMessage(status: number): string {
  switch (status) {
    case 401:
      return "Сессия истекла. Обновите страницу и войдите снова.";
    case 403:
      return "Нет доступа";
    case 404:
      return "ЛК точки не найден";
    case 422:
      return "Пароль — ровно 6 цифр";
    default:
      return "Не удалось сохранить изменения, попробуйте позже.";
  }
}

/** POST /api/auth/store-logins — завести ЛК точки. Телефон-логин назначает сервер. */
export async function createStoreLogin(input: {
  storageId: number;
  password: string;
}): Promise<CreateResult> {
  try {
    const res = await fetch(STORE_LOGINS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        storage_id: input.storageId,
        password: input.password,
      }),
    });
    if (!res.ok) return { ok: false, message: createErrorMessage(res.status) };
    const parsed = storeLoginResultSchema.safeParse(await res.json());
    // Телефон-логин — единственная ценность ответа; не разобрали → честно ошибка.
    if (!parsed.success) return { ok: false, message: createErrorMessage(0) };
    return { ok: true, login: parsed.data };
  } catch {
    return { ok: false, message: createErrorMessage(0) };
  }
}

/** PATCH /api/auth/store-logins/{user_id} — сменить пароль и/или включить-отключить. */
export async function updateStoreLogin(
  userId: number,
  patch: { password?: string; isActive?: boolean },
): Promise<MutationResult> {
  const body: { password?: string; is_active?: boolean } = {};
  if (patch.password !== undefined) body.password = patch.password;
  if (patch.isActive !== undefined) body.is_active = patch.isActive;
  // Пустой патч сервер отбивает 422 — не тратим запрос.
  if (Object.keys(body).length === 0) {
    return { ok: false, message: updateErrorMessage(422) };
  }
  try {
    const res = await fetch(`${STORE_LOGINS_ENDPOINT}/${userId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    if (!res.ok) return { ok: false, message: updateErrorMessage(res.status) };
    return { ok: true };
  } catch {
    return { ok: false, message: updateErrorMessage(0) };
  }
}
